import React, { Component } from "react";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import FamilyService from "../services/family.service";
import AuthService from "../services/auth.service";

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const chartColors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#17becf', '#8c564b', '#bcbd22', '#7f7f7f'];

export default class Overview extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isTableCollapsed: true,
      isTypeChartsCollapsed: true,
      tableData: [],
      typeEvolutionData: [],
      expenseTypes: [],
      hiddenTypes: {},
      overviewNotes: [],
      loading: true,
      isModalOpen: false,
      currentNote: '',
      currentNoteYear: null,
      currentNoteMonth: null,
    };
  }

  componentDidMount() {
    const currentUser = AuthService.getCurrentUser();
    const selectedFamily = JSON.parse(localStorage.getItem("selectedFamily"));
    let familyId;

    if (selectedFamily) {
      familyId = selectedFamily.id;
    } else if (currentUser && currentUser.families && currentUser.families.length > 0) {
      const family = currentUser.families[0];
      localStorage.setItem("selectedFamily", JSON.stringify(family));
      familyId = family.id;
    }

    if (familyId) {
      this.fetchExpensesForOverview(familyId);
      this.fetchOverviewNotes(familyId);
    } else {
      this.setState({ loading: false });
    }
  }

  async fetchOverviewNotes(familyId) {
    try {
      const response = await FamilyService.getOverviewNotesByFamilyId(familyId);
      this.setState({ overviewNotes: response.data });
    } catch (error) {
      console.error("Error fetching overview notes:", error);
    }
  }

  async fetchExpensesForOverview(familyId) {
    const promises = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    // From Jan 2024 to current month
    for (let year = 2024; year <= currentYear; year++) {
      const endMonth = (year === currentYear) ? currentMonth : 11; // current month, 11 for December
      for (let month = 0; month <= endMonth; month++) {
        promises.push(
          FamilyService.getExpensesByMonthYearFamilyId(month + 1, year, familyId)
            .then(response => {
              const typeTotals = response.data.reduce((acc, expense) => {
                const type = expense.type || 'other';
                const amount = expense.tr ? expense.amount * 0.75 : expense.amount;
                acc[type] = (acc[type] || 0) + amount;
                return acc;
              }, {});
              const totalExpense = Object.values(typeTotals).reduce((sum, value) => sum + value, 0);
              return {
                year: year,
                month: months[month],
                monthIndex: month,
                expense: totalExpense,
                typeTotals: typeTotals,
                hasData: response.data.length > 0,
                monthYearLabel: `${months[month].slice(0, 3)} ${year}`,
              };
            })
            .catch(error => {
              console.error(`Error fetching expenses for ${months[month]} ${year}:`, error);
              // Return a default object in case of an error to not break Promise.all
              return {
                year: year,
                month: months[month],
                monthIndex: month,
                expense: 0,
                typeTotals: {},
                hasData: false,
                monthYearLabel: `${months[month].slice(0, 3)} ${year}`,
              };
            })
        );
      }
    }

    const monthlyExpenses = await Promise.all(promises);

    // sort data by year and month
    monthlyExpenses.sort((a, b) => {
      if (a.year !== b.year) {
        return a.year - b.year;
      }
      return a.monthIndex - b.monthIndex;
    });

    const expenseTypesSet = new Set();
    monthlyExpenses.forEach((row) => {
      Object.keys(row.typeTotals || {}).forEach((type) => expenseTypesSet.add(type));
    });
    const expenseTypes = Array.from(expenseTypesSet);

    const typeEvolutionData = monthlyExpenses.map((row) => {
      const typeRow = {
        monthYearLabel: row.monthYearLabel,
      };
      expenseTypes.forEach((type) => {
        typeRow[type] = row.typeTotals[type] || 0;
      });
      return typeRow;
    });

    // Add id, half_expense and calculate accumulated
    for (let i = 0; i < monthlyExpenses.length; i++) {
      monthlyExpenses[i].id = i;
      monthlyExpenses[i].half_expense = monthlyExpenses[i].expense / 2;
      if (i < 11) {
        monthlyExpenses[i].accumulated = null;
      } else {
        let accumulated = 0;
        let allMonthsHaveData = true;
        for (let j = i - 11; j <= i; j++) {
          accumulated += monthlyExpenses[j].expense;
          if (!monthlyExpenses[j].hasData) {
            allMonthsHaveData = false;
          }
        }
        // Only show the 12-month accumulated value when all 12 months have data
        monthlyExpenses[i].accumulated = allMonthsHaveData ? accumulated : null;
      }
    }

    this.setState({
      tableData: monthlyExpenses.slice().reverse(), // reverse for display
      typeEvolutionData: typeEvolutionData,
      expenseTypes: expenseTypes,
      loading: false
    });
  }

  toggleTable = () => {
    this.setState(prevState => ({
      isTableCollapsed: !prevState.isTableCollapsed
    }));
  };

  toggleTypeCharts = () => {
    this.setState(prevState => ({
      isTypeChartsCollapsed: !prevState.isTypeChartsCollapsed
    }));
  };

  formatTypeLabel = (type) => {
    if (!type) return '';
    return type
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  handleTypeLegendClick = (entry) => {
    const typeKey = entry && entry.dataKey;
    if (!typeKey) return;

    this.setState((prevState) => ({
      hiddenTypes: {
        ...prevState.hiddenTypes,
        [typeKey]: !prevState.hiddenTypes[typeKey],
      },
    }));
  };

  formatTypeLegendItem = (value, entry) => {
    const typeKey = entry && entry.dataKey;
    const hidden = !!this.state.hiddenTypes[typeKey];
    return (
      <span style={{
        opacity: hidden ? 0.45 : 1,
        textDecoration: hidden ? 'line-through' : 'none',
        cursor: 'pointer',
      }}>
        {value}
      </span>
    );
  };

  openModal = (note, year, month) => {
    this.setState({
      isModalOpen: true,
      currentNote: note,
      currentNoteYear: year,
      currentNoteMonth: month,
    });
  };

  closeModal = () => {
    this.setState({
      isModalOpen: false,
      currentNote: '',
      currentNoteYear: null,
      currentNoteMonth: null,
    });
  };

  handleNoteChange = (event) => {
    this.setState({ currentNote: event.target.value });
  };

  handleSaveNote = async () => {
    const { currentNote, currentNoteYear, currentNoteMonth } = this.state;
    const selectedFamily = JSON.parse(localStorage.getItem("selectedFamily"));
    
    if (!selectedFamily) {
        console.error("No family selected");
        return;
    }
    const familyId = selectedFamily.id;

    const overviewNoteData = {
      year: currentNoteYear,
      month: currentNoteMonth,
      note: currentNote,
      paid: false, // Default to unpaid when creating new notes
    };

    try {
      await FamilyService.addOverviewNoteToFamily(familyId, overviewNoteData);
      this.fetchOverviewNotes(familyId);
      this.closeModal();
    } catch (error) {
      console.error("Error saving note:", error);
    }
  };

  togglePaidStatus = async (noteId, currentPaidStatus, year, month) => {
    const newPaidStatus = !currentPaidStatus;
    const selectedFamily = JSON.parse(localStorage.getItem("selectedFamily"));
    
    if (!selectedFamily) {
        console.error("No family selected");
        return;
    }
    const familyId = selectedFamily.id;

    try {
      if (noteId) {
        // Update existing overview note
        await FamilyService.setOverviewNotePaidStatus(noteId, newPaidStatus);
      } else {
        // Create new overview note with paid status
        const overviewNoteData = {
          year: year,
          month: month, // month is already a month name string
          note: '',
          paid: newPaidStatus,
        };
        await FamilyService.addOverviewNoteToFamily(familyId, overviewNoteData);
      }
      this.fetchOverviewNotes(familyId);
    } catch (error) {
      console.error("Error updating paid status:", error);
    }
  };

  render() {
    const { isTableCollapsed, isTypeChartsCollapsed, tableData, typeEvolutionData, expenseTypes, hiddenTypes, loading, overviewNotes, isModalOpen, currentNote } = this.state;

    if (loading) {
      return <div>Loading...</div>;
    }

    if (tableData.length === 0) { 
        return <div>No family selected or no data available.</div>;
    }

    // Filter data for LineChart to only include months with accumulated data
    // Months without data are omitted from the top charts only
    const lineChartData = tableData.filter(row => row.accumulated !== null).slice().reverse();
    const barChartData = tableData.filter(row => row.hasData).slice().reverse();

    return (
      <div className="card">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barChartData}>
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis dataKey="monthYearLabel"/>
                    <YAxis/>
                    <Tooltip/>
                    <Bar dataKey="expense" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="col-md-6">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={lineChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="monthYearLabel" />
                  <YAxis domain={['auto', 'auto']} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="accumulated" stroke="#82ca9d" connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

            <h4 className="mt-4">
              Family Expense Data
              <button className="btn btn-primary btn-sm ml-2" onClick={this.toggleTable}>
                {this.state.isTableCollapsed ? 'Show' : 'Hide'}
              </button>
            </h4>
            {!this.state.isTableCollapsed && (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Month</th>
                      <th>Overall Expense</th>
                      <th>Half Expense</th>
                      <th>Accumulated (12 months)</th>
                      <th>Notes</th>
                      <th>Paid</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row) => {
                      const note = overviewNotes.find(n => {
                        const noteDate = new Date(n.date);
                        return noteDate.getFullYear() === row.year && months[noteDate.getMonth()] === row.month;
                      });
                      return (
                      <tr key={row.id}>
                        <td>{row.year}</td>
                        <td>{row.month}</td>
                        <td>{row.expense.toFixed(2)}</td>
                        <td>{row.half_expense.toFixed(2)}</td>
                        <td>{row.accumulated ? row.accumulated.toFixed(2) : ''}</td>
                        <td>{note ? note.note : ''}</td>
                        <td>
                          {note ? (
                            <button
                              className={`btn btn-sm ${note.paid ? 'btn-success' : 'btn-danger'}`}
                              onClick={() => this.togglePaidStatus(note._id, note.paid)}
                              title={note.paid ? 'Mark as unpaid' : 'Mark as paid'}
                            >
                              {note.paid ? 'Paid' : 'Not Paid'}
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => this.togglePaidStatus(null, false, row.year, row.month)}
                              title="Mark as paid"
                            >
                              Not Paid
                            </button>
                          )}
                        </td>
                        <td>
                          <button className="btn btn-sm" title="Edit notes" onClick={() => this.openModal(note ? note.note : '', row.year, row.month)}>
                            &#x270F;
                          </button>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            )}

            <h4 className="mt-4">
              Expense Evolution by Type
              <button className="btn btn-primary btn-sm ml-2" onClick={this.toggleTypeCharts}>
                {isTypeChartsCollapsed ? 'Show' : 'Hide'}
              </button>
            </h4>
            {!isTypeChartsCollapsed && (
              <div>
                {expenseTypes.length === 0 ? (
                  <p>No expense type data available.</p>
                ) : (
                  <div className="row">
                    <div className="col-md-6">
                      <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={typeEvolutionData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="monthYearLabel" />
                          <YAxis />
                          <Tooltip />
                          <Legend onClick={this.handleTypeLegendClick} formatter={this.formatTypeLegendItem} />
                          {expenseTypes.map((type, index) => (
                            <Line
                              key={type}
                              type="monotone"
                              dataKey={type}
                              name={this.formatTypeLabel(type)}
                              stroke={chartColors[index % chartColors.length]}
                              dot={false}
                              strokeWidth={2}
                              hide={!!hiddenTypes[type]}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="col-md-6">
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={typeEvolutionData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="monthYearLabel" />
                          <YAxis />
                          <Tooltip />
                          <Legend onClick={this.handleTypeLegendClick} formatter={this.formatTypeLegendItem} />
                          {expenseTypes.map((type, index) => (
                            <Bar
                              key={type}
                              dataKey={type}
                              name={this.formatTypeLabel(type)}
                              stackId="types"
                              fill={chartColors[index % chartColors.length]}
                              hide={!!hiddenTypes[type]}
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            )}

          {isModalOpen && (
            <div className="modal" style={{ display: 'block', position: 'fixed', zIndex: 1050, left: 0, top: 0, width: '100%', height: '100%', overflow: 'auto', backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Note for {this.state.currentNoteMonth} {this.state.currentNoteYear}</h5>
                    <button type="button" className="close" onClick={this.closeModal}>
                      <span>&times;</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    <textarea
                      className="form-control"
                      rows="5"
                      value={currentNote}
                      onChange={this.handleNoteChange}
                    ></textarea>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={this.closeModal}>Close</button>
                    <button type="button" className="btn btn-primary" onClick={this.handleSaveNote}>Save changes</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
